import { useCallback, useEffect, useState } from "react";
import type { Bookmark, Highlight } from "@/types/highlight";
import {
  listBookmarks,
  deleteBookmark,
  listHighlights,
  deleteHighlight,
} from "@/services/notesService";

/**
 * MarksPanel — the bookmarks + highlights list (2026-10-06).
 *
 * From the BOOX: "Bookmarks don't have a menu." The bookmarks table has
 * existed since schema v2 and the bottom bar has had a bookmark button for
 * months, so marks were being written with nowhere to read them back. The
 * highlights table is the same shape of thing — the existing NotesPanel can
 * show highlights but nothing in the reader ever opened it (no call site
 * set notesOpen true), so in practice both were invisible. They are listed
 * together here, in one place reachable from the bottom bar.
 *
 * E-INK DESIGN NOTES — used with a thumb or stylus on a 6-13" greyscale
 * panel that cannot animate, so:
 *   - every row is >= 72px tall and the whole row is the jump target
 *   - no hover state carries meaning (hover does not exist there)
 *   - DELETION IS AN EXPLICIT BUTTON, not a swipe and not a long-press.
 *     Thomas asked for swipe-or-long-press but also for "no fine-grained
 *     gestures"; on e-ink the gesture is the less reliable of the two, and
 *     a mis-timed long-press that destroys a bookmark is not recoverable.
 *     Two-step confirm, for the same reason.
 *   - borders rather than shadows: shadows dither badly at 16 grey levels
 *   - nothing depends on a transition to be understood
 */

interface Props {
  bookId: string;
  /** Jump to a CFI. The caller closes the panel and drives the rendition. */
  onJumpTo: (cfi: string) => void;
  onClose: () => void;
  /** Toggle a bookmark on the page currently displayed. Supplied by the
   *  reader, which is the only thing that knows the current CFI. */
  onAddBookmark?: () => Promise<void> | void;
  /** Whether the displayed page already carries a bookmark — drives the
   *  button's label so it never lies about what it will do. */
  isPageBookmarked?: boolean;
  /** Render inside the bottom bar's panel slot instead of as a
   *  floating overlay: no fixed positioning, no scrim, inherits the
   *  bar's background. 2026-10-06 — Thomas asked for panels to sit on
   *  top of the progress row as part of the bar assembly. */
  inline?: boolean;
}

type Tab = "bookmarks" | "highlights";

export function MarksPanel({
  bookId,
  onJumpTo,
  onClose,
  onAddBookmark,
  isPageBookmarked,
  inline,
}: Props) {
  const [tab, setTab] = useState<Tab>("bookmarks");
  const [bookmarks, setBookmarks] = useState<Bookmark[] | null>(null);
  const [highlights, setHighlights] = useState<Highlight[] | null>(null);
  // id of the row currently asking "Delete?" — only ever one at a time.
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const [b, h] = await Promise.all([
        listBookmarks(bookId),
        listHighlights(bookId),
      ]);
      // Newest first: the mark you just made is the one you are most
      // likely to want back.
      setBookmarks(
        [...b].sort((x, y) => (y.createdAt ?? 0) - (x.createdAt ?? 0)),
      );
      setHighlights(h);
    } catch (err) {
      // Never leave the panel in a permanent spinner — an empty list beats
      // a hang.
      // eslint-disable-next-line no-console
      console.warn("[pagevoice] MarksPanel load failed", err);
      setBookmarks((prev) => prev ?? []);
      setHighlights((prev) => prev ?? []);
    }
  }, [bookId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onDelete = async (kind: Tab, id: string) => {
    try {
      if (kind === "bookmarks") await deleteBookmark(id);
      else await deleteHighlight(id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[pagevoice] delete failed", err);
    }
    setConfirmId(null);
    await reload();
  };

  const rows = tab === "bookmarks" ? bookmarks : highlights;

  return (
    <>
      {!inline && (
      <div
        className="fixed inset-0 z-20 bg-black/20"
        onClick={onClose}
        aria-hidden
      />
      )}
      <div
        className={inline ? "pv-fg flex min-h-0 w-full flex-col overflow-y-auto" : "pv-surface pv-border pv-fg fixed inset-x-0 z-30 mx-auto flex max-h-[62vh] w-[92vw] max-w-lg flex-col rounded-lg border"}
        /* 2026-10-06: was centred on screen; now anchored above the bar. */
        role="dialog"
        aria-label="Bookmarks and highlights"
        style={inline ? {} : {
          borderWidth: "2px",
          bottom: "calc(var(--pv-bar-h, 152px) + 8px)",
        }}
      >
        {/* 2026-10-07, Thomas: "Remove X on marks search and settings.
            Remove whole line." The close row is gone entirely, not just
            emptied — on e-ink every reserved row costs reading area.
            VERIFIED before removing it that he cannot be stranded: the
            Marks tile in the bar toggles this panel shut on a second tap
            (measured closedBySecondTap=true), and the hide caret collapses
            the whole bar assembly. Two ways out, so the X was the third. */}
        {/* Tabs. 56px tall, full-width halves — a thumb target. The
            selected one is marked by a solid underline plus bold rather
            than a colour wash, which survives greyscale. */}
        <div className="pv-border flex border-b">
          {(["bookmarks", "highlights"] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setTab(t);
                setConfirmId(null);
              }}
              aria-pressed={tab === t}
              className="flex-1 text-base"
              style={{
                minHeight: "56px",
                fontWeight: tab === t ? 700 : 400,
                borderBottom:
                  tab === t
                    ? "3px solid var(--pv-fg)"
                    : "3px solid transparent",
              }}
            >
              {t === "bookmarks"
                ? `Bookmarks${bookmarks ? ` (${bookmarks.length})` : ""}`
                : `Highlights${highlights ? ` (${highlights.length})` : ""}`}
            </button>
          ))}
        </div>

        {/* The "Bookmark this page" row that used to sit here is gone
            (2026-10-06, Thomas: "Remove bookmark this page button").
            The top-right bookmark toggle has shipped and is the single
            control for creating one — which also removes the two-controls-
            that-could-disagree question for good. This panel is now purely
            a list. onAddBookmark/isPageBookmarked stay on the props so the
            reader keeps one implementation of the toggle. */}
        {/* 2026-10-06, Thomas: "no bookmarks no highlight text. If 0 just
            make section small. If larger show bookmarks or highlights."
            So an empty section renders as a short blank strip rather than
            announcing its own emptiness — the panel sizes itself to its
            content. Loading keeps a bare strip too; a spinner or the word
            "Loading" would flash on e-ink for no information. */}
        {rows === null ? (
          <div style={{ height: "24px" }} />
        ) : rows.length === 0 ? (
          <div style={{ height: "24px" }} />
        ) : (
          <ul className="flex-1 overflow-y-auto">
            {rows.map((row) => {
              const isBookmark = tab === "bookmarks";
              const bm = row as Bookmark;
              const hl = row as Highlight;
              const jumpCfi = isBookmark ? bm.cfi : hl.cfiRange;
              const primary = isBookmark
                ? bm.label?.trim() ||
                  bm.chapterLabel?.trim() ||
                  bm.href ||
                  "Bookmark"
                : hl.text?.trim() || "(empty highlight)";
              const secondary = isBookmark
                ? formatWhen(bm.createdAt)
                : [hl.chapterLabel?.trim(), formatWhen(hl.createdAt)]
                    .filter(Boolean)
                    .join(" · ");
              const confirming = confirmId === row.id;
              return (
                <li
                  key={row.id}
                  className="pv-border border-b"
                  style={{ borderBottomWidth: "1px" }}
                >
                  <div className="flex items-stretch">
                    {/* Jump target — the whole row. */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (jumpCfi) onJumpTo(jumpCfi);
                      }}
                      className="flex-1 px-4 py-3 text-left"
                      style={{ minHeight: "72px" }}
                    >
                      <div
                        className="text-base"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {primary}
                      </div>
                      {secondary ? (
                        <div className="pv-fg-soft pt-1 text-xs">
                          {secondary}
                        </div>
                      ) : null}
                    </button>

                    {/* Two-step delete. Step one arms it, step two does
                        it. No gesture, nothing to mis-time. */}
                    {confirming ? (
                      <div className="flex items-stretch">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void onDelete(tab, row.id);
                          }}
                          className="px-4 text-base font-bold"
                          style={{
                            minWidth: "88px",
                            borderLeft: "2px solid var(--pv-fg)",
                          }}
                          aria-label="Confirm delete"
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmId(null);
                          }}
                          className="px-3 text-base"
                          style={{
                            minWidth: "72px",
                            borderLeft: "1px solid var(--pv-fg)",
                          }}
                        >
                          Keep
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmId(row.id);
                        }}
                        className="px-4 text-base"
                        style={{
                          minWidth: "72px",
                          borderLeft: "1px solid var(--pv-fg)",
                        }}
                        aria-label="Remove"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

/** Short, human date. Bookmarks carry epoch millis; be tolerant of nulls
 *  and of seconds-vs-millis, since older rows predate the current writer. */
function formatWhen(t?: number): string {
  if (!t || !Number.isFinite(t)) return "";
  const ms = t < 1e12 ? t * 1000 : t;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
